import { Button, Heading, Icon, IconButton, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import ImportCSVButton from "../../components/Buttons/ImportCSVButton";
import { useEffect, useState } from "react";
import { FaCheck, FaEdit, FaPlus, FaSync, FaTrash } from "react-icons/fa";
import { RiContactsBookFill } from "react-icons/ri";
import { useSelector } from "react-redux";
import APIDropdown from "../../components/Select/APIDropdown";
import { GET, POST } from "../../utils/ApiRequestProvider";

export default function Contacts() {
    const business = useSelector((state: any) => state.selections.value?.business);
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [items, setItems] = useState<Array<any>>([]);
    const [loading, setLoading] = useState(true);

    const [form, setForm] = useState<any>({
        name: '',
        chartOfAccountId: 0,
        paymentMethodId: 0,
        vatgstRateId: 0,
        status: '',
        isSupplier: false
    });

    async function Save() {
        try {
            setLoading(true);

            const response = await POST(
                '/api/contacts',
                {
                    ...form,
                    businessId: business.id
                }
            );
            toast({
                status: response.status,
                title: response.message,
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });

            setLoading(false);

            if (response.status === 'success') {
                FetchList();
                setForm({
                    name: '',
                    chartOfAccountId: 0,
                    paymentMethodId: 0,
                    vatgstRateId: 0,
                    status: '',
                    isSupplier: false
                });
                onClose();
            }
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
    }

    async function Edit(data: any) {
        try {
            setForm({
                id: data.id,
                name: data.name,
                chartOfAccountId: data.defaultCategory?.id,
                paymentMethodId: data.defaultPaymentMethod?.id,
                vatgstRateId: data.defaultVATGSTRate?.id,
                status: data.status,
                isSupplier: data.isSupplier
            });
            onOpen();
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
    }

    async function Delete(data: any) {
        try {
            setForm({
                id: data.id,
                name: data.name,
                chartOfAccountId: data.defaultCategory?.id,
                paymentMethodId: data.defaultPaymentMethod?.id,
                vatgstRateId: data.defaultVATGSTRate?.id,
                status: data.status,
                isSupplier: data.isSupplier
            });
            onOpen();
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
    }

    async function CloseModel() {
        setForm({
            name: '',
            chartOfAccountId: 0,
            paymentMethodId: 0,
            vatgstRateId: 0,
            status: '',
            isSupplier: false
        });
        onClose();
    }

    async function RefreshPage() {
        setLoading(true);
        setTimeout(() => {FetchList();setLoading(false)}, 1000);
    }

    useEffect(() => {
        FetchList();
        setTimeout(() => {setLoading(false)}, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [business]);

    async function FetchList() {
        try {
            if (business) {
                const res = await GET(`/api/contacts/${business.id}`);
                setItems(res.data);
            }
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
    }

    return (
        <Stack w={'full'} justify={"center"} align={"center"} spacing={6}>
            <Stack
                direction={{ base: 'column', md: 'column', lg: 'column', xl: 'row' }}
                justify={'space-between'}
                boxShadow={'base'}
                rounded={'xl'}
                w={'98%'}
                p={4}
                spacing={{ base: 6, md: 6, lg: 6, xl: 0 }}
                align={'center'}
            >
                <Stack
                    align={'flex-start'}
                    justify={'start'}
                    spacing={4}
                >
                    <Stack direction={'row'}>
                        <Icon
                            as={RiContactsBookFill}
                            fontSize={'5xl'}
                            color={'primary.400'}
                        />
                        <Stack spacing={1}>
                            <Heading fontSize={'xl'}>Contacts</Heading>
                            <Text fontSize={'sm'}>Here you can see all your contacts imported from online accounting softwares</Text>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack align={{ lg: 'flex-start', xl: 'flex-end' }} justify={'end'} spacing={4}>
                    <Stack direction={'row'}>
                        <Button
                            size={'sm'}
                            leftIcon={<FaPlus />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={onOpen}
                        >
                            Add Contacts
                        </Button>
                        <ImportCSVButton
                            endpoint={business ? `/api/contacts/${business.id}/import` : '/api/contacts/import'}
                            label={'Import Contacts'}
                            onImported={() => FetchList()}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                        />
                    </Stack>
                    <Stack direction={'row'}>
                        <Button
                            size={'sm'}
                            leftIcon={<FaSync />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={RefreshPage}
                            isLoading={loading}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>
            </Stack>

            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4} spacing={4}>
                <TableContainer h={'sm'} overflowY={'scroll'}>
                    <Table size='sm'>
                        <Thead position={'sticky'} top={'0%'} zIndex={1} h={'50px'} bg={'primary.400'} alignItems={'flex-start'}>
                            <Tr>
                                <Th
                                    color={'white'}
                                >
                                    Contact Name
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Default Category
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Default Payment Method
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Default VAT/GST Rate
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Status
                                </Th>
                                <Th
                                    color={'white'}
                                    textAlign={'center'}
                                >
                                    Action
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {
                                loading ? (
                                    <Tr>
                                        <Td textAlign={'center'} colSpan={6}>Loading...</Td>
                                    </Tr>
                                ) :
                                items?.length === 0 ? (
                                    <Tr>
                                        <Td textAlign={'center'} colSpan={6}>No Data Found</Td>
                                    </Tr>
                                ) :
                                    (
                                        items.map(item => (
                                            <Tr>
                                                <Td>{item.name}</Td>
                                                <Td>{item.defaultCategory?.category}</Td>
                                                <Td>{item.defaultPaymentMethod?.displayName}</Td>
                                                <Td>{item.defaultVATGSTRate?.name}</Td>
                                                <Td>{item.status}</Td>
                                                <Td as={Stack} direction={'row'} justify={'center'} spacing={4}>
                                                    <IconButton
                                                        aria-label="Edit Contact"
                                                        icon={<FaEdit />}
                                                        size={'md'}
                                                        bg={'transparent'}
                                                        color={'primary.400'}
                                                        rounded={'full'}
                                                        onClick={() => Edit(item)}
                                                    />
                                                    <IconButton
                                                        aria-label="Edit Contact"
                                                        icon={<FaTrash />}
                                                        size={'md'}
                                                        bg={'transparent'}
                                                        color={'primary.400'}
                                                        rounded={'full'}
                                                        onClick={() => Delete(item)}
                                                    />
                                                </Td>
                                            </Tr>
                                        ))
                                    )
                            }
                        </Tbody>
                    </Table>
                </TableContainer>
            </Stack>
            <Modal
                isCentered
                size={'3xl'}
                isOpen={isOpen}
                onClose={CloseModel}
            >
                <ModalOverlay
                    bg='blackAlpha.300'
                    backdropFilter='blur(10px)'
                />
                <ModalContent>
                    <ModalHeader
                        as={Stack}
                        direction={'row'}
                        borderBottom={'2px'}
                        borderBottomColor={'gray.300'}
                    >
                        <Icon
                            as={RiContactsBookFill}
                            fontSize={'5xl'}
                            color={'primary.400'}
                        />
                        <Stack spacing={1}>
                            <Heading fontSize={'xl'}>Add Contacts</Heading>
                            <Text fontSize={'sm'}>Add new contacts from here</Text>
                        </Stack>
                    </ModalHeader>
                    <ModalBody as={Stack} direction={'row'} spacing={4} py={8}>
                        <Stack spacing={4} w={'full'}>
                            <Input
                                w={'full'}
                                type={'text'}
                                size={'md'}
                                placeholder={'Name'}
                                value={form.name}
                                onChange={(e: any) => setForm({ ...form, name: e.target.value })}
                            />
                            <APIDropdown
                                w={'full'}
                                placeholder={'Default Category'}
                                fetchurl={`/api/chartofaccounts/${business?.id}`}
                                itemselect={(item) => setForm({ ...form, chartOfAccountId: item.id })}
                                valuekey={'id'}
                                textkey={'category'}
                            />
                            <APIDropdown
                                w={'full'}
                                placeholder={'Default Payment Method'}
                                fetchurl={`/api/paymentmethods/${business?.id}`}
                                itemselect={(item) => setForm({ ...form, paymentMethodId: item.id })}
                                valuekey={'id'}
                                textkey={'displayName'}
                            />
                        </Stack>
                        <Stack spacing={4} w={'full'}>
                            <APIDropdown
                                w={'full'}
                                placeholder={'Default VAT/GST Rate'}
                                fetchurl={`/api/vatgst/${business?.id}`}
                                itemselect={(item) => setForm({ ...form, vatgstRateId: item.id })}
                                valuekey={'id'}
                                textkey={'name'}
                            />
                            <Select
                                w={'full'}
                                placeholder={'Status'}
                                value={form.status}
                                onChange={(e: any) =>
                                    setForm({ ...form, status: e.target.value })
                                }
                            >
                                <option value={'Active'}>Active</option>
                                <option value={'Inactive'}>Inactive</option>
                            </Select>
                            <Select w={'full'} value={form.isSupplier === true ? 'Supplier' : 'Customer'} 
                            onChange={(e: any) => setForm({ ...form, isSupplier: e.target.value === 'Supplier' ? true : false })}>
                                <option value={'Customer'} selected={form.isSupplier === false}>Customer</option>
                                <option value={'Supplier'} selected={form.isSupplier === true}>Supplier</option>
                            </Select>
                        </Stack>
                    </ModalBody>
                    <ModalFooter
                        as={Stack}
                        direction={'row'}
                        spacing={4}
                        borderTop={'2px'}
                        borderTopColor={'gray.300'}
                    >
                        <Button
                            w={'full'}
                            onClick={onClose}
                        >
                            Close
                        </Button>
                        <Button
                            w={'full'}
                            onClick={Save}
                            leftIcon={<FaCheck />}
                            colorScheme={'primary'}
                            isLoading={loading}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Stack >
    )
}